// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { DEFAULT_COMPONENT_ICONS, FluentThemeProvider } from '@azure/communication-react';
import { initializeIcons, registerIcons } from '@fluentui/react';
import { MessageBar, MessageBarType, Stack } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { LoginForm } from '../../components/LoginForm';
import TopNav from '../../components/TopNav';
import { useAuthStatus } from '../../utils/useAuthStatus';

initializeIcons();
registerIcons({ icons: DEFAULT_COMPONENT_ICONS });

function App(): JSX.Element {
  const {
    isAuthenticated,
    isAdmin,
    authError,
    setAuthError,
    handleLoginSuccess,
    handleLoginFailure,
    hasCheckedStatus
  } = useAuthStatus({
    authProvider: 'login',
    redirectUri: '/app/v2'
  });

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/app/v2') ? '/app/v2' : '/app/v1';

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (isAdmin) {
      navigate(`${basePath}/admin`);
    } else {
      navigate(`${basePath}/interview`);
    }
  }, [isAuthenticated, basePath, navigate]);

  return (
    <FluentThemeProvider>
      <Stack
        tokens={{ childrenGap: 32 }}
        styles={{
          root: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <TopNav isAuthenticated={isAuthenticated} isAdmin={isAdmin} />
        <Stack tokens={{ childrenGap: 32 }} styles={{ root: { flexGrow: 1 } }}>
          {authError && (
            <MessageBar
              messageBarType={MessageBarType.blocked}
              isMultiline={false}
              onDismiss={() => setAuthError(null)}
            >
              {authError}
            </MessageBar>
          )}
          {!isAuthenticated && hasCheckedStatus && (
            <LoginForm onLogin={handleLoginSuccess} onFail={handleLoginFailure} />
          )}
        </Stack>
      </Stack>
    </FluentThemeProvider>
  );
}

export default App;
